import React, { useEffect, useId, useState } from 'react';
import { EditAdminUserForm } from '../components/BtAdminPanel/EditAdminUsers';
import { AdminUserForm } from '../components/BtAdminPanel/AdminUsers';
import { DeleteOrg } from '../components/BtAdminPanel/DeleteOrganisation';
import ResetMfa from '../components/BtAdminPanel/ResetMfa';
import AssignedModuleForm from '../components/BtAdminPanel/AssignedModules';
import { EditUserForm } from '../components/AdminPanel/EditUser';
import IModuleList from '../utils/ModuleList/ModuleList.interface';
import IUserList from '../utils/UserList/UserList.interface';
import OrgUpdate from '../components/BtAdminPanel/OrgUpdate';
import AllOrganizationAdmin from '../utils/OrganizationUser/AllOrganisationUser';
import Button from '../library/Button/button';
import ImageTag from '../library/ImageTag/ImageTag';
import domain from '../assets/img/domain.png';
import user from '../assets/img/user.png';
import { OrgUserForm } from '../components/BtAdminPanel/OrgUsers';
import EyeToggle from '../library/Button/EyeToggle';
import DeactiveAdmin from '../utils/Deactivate/DeactivateAdmin';
import { responseMessages } from '../utils/Helper/enums';
import { handleApiResponse } from '../utils/Helper/apiResponseHandler';
import CustomPopup from '../components/BtAdminPanel/CustomPopup';

interface Props {
  partner: IUserList;
  onChangePartner: (partner: IUserList) => void;
  onSwitchAdmin: (oldEmail: string, updatedPartner: IUserList) => void;
  onDeletePartner: (partner: IUserList) => void;
  id: string;
  highlightedPartnerEmail: string | null;
}

const PartnerAdminRow: React.FC<Props> = ({
  partner,
  onChangePartner,
  onSwitchAdmin,
  onDeletePartner,
  id,
  highlightedPartnerEmail
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showOrgUpdate, setShowOrgUpdate] = useState<boolean>(false);
  const [showUsers, setshowUsers] = useState<boolean>(false);
  const [isDomainShowing, setIsDomainShowing] = useState<boolean>(false);
  const [isUsernShowing, setIsUserShowing] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [usersData, setUsersData] = useState<IUserList[]>([]);
  const uniqueId = useId();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [fetchUsersCounter, setFetchUsersCounter] = useState(0);

  useEffect(() => {
    if (showOrgUpdate) {
      const initialVisibility: Record<string, boolean> = {};

      usersData.forEach((user) => {
        if (user.email) {
          initialVisibility[user.email] = user.isActive ?? false;
        }
      });
      setIsVisible(initialVisibility);
    }
  }, [usersData, showOrgUpdate]);

  const handleDeactivateAdmin = async (email: string) => {
    // Directly get the current status from the partner's isActive field
    const currentStatus = isVisible[email] ?? partner.isActive ?? false;

    const newState = !currentStatus;

    // Update the visibility state
    setIsVisible((prev) => ({
      ...prev,
      [email]: newState
    }));

    try {
      const userObj = new DeactiveAdmin({
        email,
        isActive: newState
      });

      const updateStatus = await userObj.deactivateAdmin();
      const errorMessage = responseMessages.SomethingWentWrong;
      const successMessage = updateStatus.body;
      const response = handleApiResponse(updateStatus.statusCode, errorMessage, successMessage);
      const responseBody = response.body;
      // If toggled user is the main partner, update the partner
      if (email === partner.email) {
        onChangePartner({ ...partner, isActive: newState });
      } else {
        // Optionally: update a sub-user locally if needed
        const updatedUser = usersData.find((user) => user.email === email);
        if (updatedUser) {
          handleUserDetailChangeOnUI({ ...updatedUser, isActive: newState });
        }
      }
      setAlertMessage(responseBody);
      setShowAlert(true);
    } catch (error) {
      console.error('Error deactivating user:', error);
      setAlertMessage('Error deactivating user');

      // Optionally revert local UI
      setIsVisible((prev) => ({
        ...prev,
        [email]: currentStatus
      }));
    }
  };

  const handleStatusChange = (status: number | string) => {
    onChangePartner({
      ...partner,
      status: status.toString()
    });
  };

  const handleDeleteUser = (email: string) => {
    const updatedUsers = usersData.filter((item) => item.email !== email);
    setUsersData(updatedUsers);
    setEmailList((prev) => prev.filter((item) => item !== email));

    const newStatus = updatedUsers.length + 1;
    handleStatusChange(newStatus);
  };

  const handleUserAdd = () => {
    const currentCount = parseInt(partner.status ?? '1', 10);
    handleStatusChange(currentCount + 1);
  };

  const handleUserDetailChangeOnUI = (userDetails: IUserList) => {
    setUsersData((prev) =>
      prev.map((item) => (item.email === userDetails.email ? { ...item, ...userDetails } : item))
    );
  };

  const handleUpdateDomain = (domains: Array<string>) => {
    onChangePartner({
      ...partner,
      domains
    });
  };

  const onDelete = () => {
    onDeletePartner(partner);
  };

  const handleSwitchAdmin = (newAdminEmail: string) => {
    const newAdmin = usersData.find((u) => u.email === newAdminEmail);
    const oldEmail = partner.email!;
    const oldPartner: IUserList = {
      ...partner,
      role: 'User',
      orgEmail: partner.email,
      buttonStatus: null
    };
    setUsersData((prev) => [...prev.filter((u) => u.email !== newAdminEmail), oldPartner]);
    onSwitchAdmin(oldEmail, {
      ...partner,
      email: newAdmin?.email ?? newAdminEmail,
      name: newAdmin?.name ?? partner.name,
      phone: newAdmin?.phone ?? partner.phone,
      role: 'Admin'
    });
  };

  useEffect(() => {
    const getAdminUsers = async () => {
      setIsLoading(true);
      const organizationAdminObj = new AllOrganizationAdmin({ email: partner.email ?? '' });
      const result = await organizationAdminObj.getAllAdminUser();
      const partnerEmailList: string[] = result.map((user) => {
        return user?.email ?? '';
      });
      partnerEmailList.push(partner.email ?? '');
      setEmailList(partnerEmailList);
      handleStatusChange(result.length + 1);
      setUsersData(result);
      setIsLoading(false);
    };
    if (fetchUsersCounter > 0) {
      void getAdminUsers();
    }
  }, [fetchUsersCounter]);

  // Method to close the popup on clicking the close button
  const popupCloseHandler = (e: boolean) => {
    setShowAlert(false);
  };
  return (
    <tr
      id={id}
      className={`transition-colors ${
        highlightedPartnerEmail === partner?.email ? 'bg-gray' : 'even:bg-[#f8f8f8]'
      }`}>
      <td className="text-left text-base py-2 pl-6">{partner?.companyName}</td>
      <td className="text-left text-base py-2 pl-6">{partner?.email}</td>
      <td className="text-center text-base py-2">{partner?.status}</td>
      <td className="text-center text-base py-2">{partner?.module}</td>
      <td className="min-w-[150px] text-center text-base py-2">
        {partner.buttonStatus === null ? (
          <EditUserForm
            orgPartnerCompanyEmail={''}
            userName={partner.name}
            orgJobTitle={partner.role}
            orgPartnerCompanyName={partner.companyName}
            userEmail={partner.email}
            disabled={false}
            userPhone={partner.phone}
            onEditUser={handleUserDetailChangeOnUI}
          />
        ) : (
          <div className="w-fit mx-auto">
            <div className="flex w-fit pb-1 min-w-fit [&>*]:flex-shrink-0 gap-[2px]">
              <EditAdminUserForm partner={partner} onChange={onChangePartner} />
              <DeleteOrg
                organisationName={partner.companyName}
                organisationEmail={partner.email}
                onDelete={onDelete}
              />
              <AdminUserForm
                organisationName={partner.companyName ?? ''}
                organisationEmail={partner.email ?? ''}
                domains={partner.domains}
                onUserAdd={handleUserAdd}
                disabled={!partner.isActive}
              />
              <ResetMfa userEmail={partner.email} disabled={false} />
              <Button
                id={`domail-update-${uniqueId}`}
                aria-label="Edit Organisation"
                className="bg-transparent"
                onclick={() => {
                  setFetchUsersCounter((c) => c + 1);
                  setShowOrgUpdate(true);
                }}
                onMouseEnter={() => setIsDomainShowing(true)}
                onMouseLeave={() => setIsDomainShowing(false)}>
                <ImageTag src={domain} width={'19px'} alt={''} name={'Domain'} />
                {isDomainShowing && (
                  <div className="group flex relative">
                    <span
                      className="px-1 py-1 group-hover:opacity-100 transition-opacity text-sm text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800 absolute -top-24 -left-1/2
              -translate-x-1/2 translate-y-full m-4 mx-auto">
                      Organization Domains
                    </span>
                  </div>
                )}
              </Button>
              <Button
                id={`user-update-${uniqueId}`}
                aria-label="Edit Organisation"
                className="bg-transparent"
                onclick={() => {
                  setFetchUsersCounter((c) => c + 1);
                  setshowUsers(true);
                }}
                onMouseEnter={() => {
                  setIsUserShowing(true);
                }}
                onMouseLeave={() => setIsUserShowing(false)}>
                <ImageTag src={user} width={'19px'} alt={''} name={'User'} />
                {isUsernShowing && (
                  <div className="group flex relative">
                    <span
                      className="px-1 py-1 group-hover:opacity-100 transition-opacity text-sm text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800 absolute -top-24 -left-3/4
              -translate-x-1/2 translate-y-full m-4 mx-auto">
                      Organization Users
                    </span>
                  </div>
                )}
              </Button>
              {showOrgUpdate && (
                <OrgUpdate
                  isLoading={isLoading}
                  show={showOrgUpdate}
                  setShow={setShowOrgUpdate}
                  emailList={emailList}
                  partner={partner}
                  onDomainChange={handleUpdateDomain}
                />
              )}
              {showUsers && (
                <OrgUserForm
                  usersData={usersData}
                  query={''}
                  disabled={false}
                  show={showUsers}
                  setShow={setshowUsers}
                  isLoading={isLoading}
                  onDeleteUser={handleDeleteUser}
                  onEditUser={handleUserDetailChangeOnUI}
                  isPartnerActive={partner.isActive!}
                  onRefreshUsers={() => setFetchUsersCounter((c) => c + 1)}
                  onSwitchAdmin={handleSwitchAdmin}
                />
              )}
              <EyeToggle
                onClick={() => {
                  void handleDeactivateAdmin(partner.email!);
                }}
                isVisible={!partner.isActive}
              />
              <CustomPopup onClose={popupCloseHandler} show={showAlert} width={'w-41%'}>
                <div className="max-h-30% ">
                  <p className="mr-0 mb-0.5 ml-0 text-[17px]">{alertMessage}</p>
                </div>
              </CustomPopup>
            </div>
            <AssignedModuleForm
              organisationName={partner.companyName ?? ''}
              partner={partner}
              onChangePartner={onChangePartner}
            />
          </div>
        )}
      </td>
    </tr>
  );
};

export default PartnerAdminRow;
